"use client";

import { cn, useBooleanState, useDisclosure } from "@gettactic/helpers";
import { CheckIcon, ChevronLeft, ChevronRight } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Drawer } from "../../Drawer";
import { ModalCancelButton } from "../buttons/ModalCancelButton";
import { ModalConfirmButton } from "../buttons/ModalConfirmButton";
import { ConfirmModal } from "../ConfirmModal";
import { Modal } from "../Modal";
import { runExclusive } from "./runExclusive";
import {
  UseWizardOptions,
  UseWizardReturn,
  WizardFooterRenderProps,
  WizardModalProps,
  WizardProgressRenderProps
} from "./WizardModal.types";

/**
 * Hook to manage wizard state
 */
export function useWizard(options: UseWizardOptions): UseWizardReturn {
  const { totalSteps, initialStep = 0, onStepChange } = options;
  const [currentStep, setCurrentStep] = useState(initialStep);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const progress =
    totalSteps > 1 ? (currentStep / (totalSteps - 1)) * 100 : 100;

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      onStepChange?.(newStep);
    }
  }, [currentStep, onStepChange]);

  const goNext = useCallback(() => {
    if (currentStep < totalSteps - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      onStepChange?.(newStep);
    }
  }, [currentStep, totalSteps, onStepChange]);

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalSteps) {
        setCurrentStep(index);
        onStepChange?.(index);
      }
    },
    [totalSteps, onStepChange]
  );

  const reset = useCallback(() => {
    setCurrentStep(initialStep);
    onStepChange?.(initialStep);
  }, [initialStep, onStepChange]);

  return {
    currentStep,
    totalSteps,
    isFirstStep,
    isLastStep,
    goBack,
    goNext,
    goToStep,
    reset,
    progress
  };
}

/**
 * Progress indicator component - Dots variant
 */
const WizardProgressDots: React.FC<WizardProgressRenderProps> = ({
  currentStep,
  steps,
  goToStep,
  allowStepClick
}) => {
  return (
    <div className="flex items-center justify-center gap-2">
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isCurrent = index === currentStep;

        return (
          <button
            key={step.id}
            type="button"
            disabled={!allowStepClick}
            onClick={() => allowStepClick && goToStep(index)}
            className={cn(
              "h-2.5 w-2.5 rounded-full transition-all",
              isCompleted && "bg-primary",
              isCurrent && "bg-primary ring-2 ring-primary/30 ring-offset-2",
              !isCompleted && !isCurrent && "bg-neutral-300",
              allowStepClick && "cursor-pointer hover:opacity-80",
              !allowStepClick && "cursor-default"
            )}
            aria-label={`Step ${index + 1}: ${step.label}`}
          />
        );
      })}
    </div>
  );
};

type WizardStepState = "completed" | "current" | "upcoming";

const wizardStepStateOf = (
  index: number,
  currentStep: number
): WizardStepState =>
  index < currentStep
    ? "completed"
    : index === currentStep
      ? "current"
      : "upcoming";

const wizardMarkerClass = (state: WizardStepState) =>
  cn(
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-semibold text-sm transition-colors",
    state === "completed" &&
      "border-primary bg-primary text-primary-foreground",
    state === "current" && "border-primary bg-primary text-primary-foreground",
    state === "upcoming" && "border-neutral-200 bg-white text-neutral-400"
  );

/**
 * Progress indicator - Steps variant (compact horizontal, themis-style).
 * Circular markers (number / check) connected by lines, with inline labels.
 */
const WizardProgressSteps: React.FC<WizardProgressRenderProps> = ({
  currentStep,
  steps,
  goToStep,
  allowStepClick
}) => (
  <nav aria-label="Progress">
    <ol className="flex items-center py-1">
      {steps.map((step, index) => {
        const state = wizardStepStateOf(index, currentStep);
        const isLast = index === steps.length - 1;
        const canClick = allowStepClick && index <= currentStep;
        return (
          <li
            key={step.id}
            className={cn("flex items-center", !isLast && "flex-1")}
          >
            <button
              type="button"
              disabled={!canClick}
              onClick={() => canClick && goToStep(index)}
              className={cn(
                "flex items-center gap-2",
                canClick ? "cursor-pointer" : "cursor-default"
              )}
              aria-current={state === "current" ? "step" : undefined}
            >
              <span className={wizardMarkerClass(state)}>
                {state === "completed" ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </span>
              <span
                className={cn(
                  "font-inter hidden whitespace-nowrap font-medium text-sm sm:inline",
                  state === "upcoming" ? "text-neutral-400" : "text-neutral-900"
                )}
              >
                {step.label}
              </span>
            </button>
            {!isLast ? (
              <span
                aria-hidden
                className={cn(
                  "mx-3 h-0.5 flex-1 rounded-full",
                  index < currentStep ? "bg-primary" : "bg-neutral-200"
                )}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  </nav>
);

/**
 * Progress indicator - vertical descriptive stepper for the sidebar layout.
 */
const WizardSidebarSteps: React.FC<WizardProgressRenderProps> = ({
  currentStep,
  steps,
  goToStep,
  allowStepClick
}) => (
  <ol className="flex flex-col">
    {steps.map((step, index) => {
      const state = wizardStepStateOf(index, currentStep);
      const isLast = index === steps.length - 1;
      const canClick = allowStepClick && index <= currentStep;
      return (
        <li key={step.id} className="relative">
          <button
            type="button"
            disabled={!canClick}
            onClick={() => canClick && goToStep(index)}
            className={cn(
              "flex w-full gap-3 rounded-lg text-left",
              canClick ? "cursor-pointer" : "cursor-default"
            )}
            aria-current={state === "current" ? "step" : undefined}
          >
            <div className="flex flex-col items-center">
              <span className={wizardMarkerClass(state)}>
                {state === "completed" ? (
                  <CheckIcon className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </span>
              {!isLast ? (
                <span
                  aria-hidden
                  className={cn(
                    "w-0.5 flex-1 rounded-full",
                    index < currentStep ? "bg-primary" : "bg-neutral-200"
                  )}
                />
              ) : null}
            </div>
            <div className={cn("min-w-0 pt-1.5", !isLast && "pb-6")}>
              <p
                className={cn(
                  "font-inter truncate font-semibold text-sm",
                  state === "upcoming" ? "text-neutral-400" : "text-neutral-900"
                )}
              >
                {step.label}
              </p>
              {step.description ? (
                <p className="font-inter mt-0.5 text-neutral-500 text-xs leading-snug">
                  {step.description}
                </p>
              ) : null}
            </div>
          </button>
        </li>
      );
    })}
  </ol>
);

/**
 * Progress indicator component - Bar variant
 */
const WizardProgressBar: React.FC<WizardProgressRenderProps> = ({
  currentStep,
  totalSteps,
  steps
}) => {
  const progress =
    totalSteps > 1 ? (currentStep / (totalSteps - 1)) * 100 : 100;

  return (
    <div className="w-full">
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-medium text-neutral-700">
          Step {currentStep + 1} of {totalSteps}
        </span>
        <span className="text-neutral-500">{steps[currentStep]?.label}</span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-neutral-200">
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Default footer component
 */
const WizardFooter: React.FC<{
  props: WizardFooterRenderProps;
  backLabel: React.ReactNode;
  nextLabel: React.ReactNode;
  completeLabel: React.ReactNode;
  cancelLabel: React.ReactNode;
  showCancel: boolean;
  backProps?: WizardModalProps["backProps"];
  nextProps?: WizardModalProps["nextProps"];
  completeProps?: WizardModalProps["completeProps"];
  cancelProps?: WizardModalProps["cancelProps"];
}> = ({
  props,
  backLabel,
  nextLabel,
  completeLabel,
  cancelLabel,
  showCancel,
  backProps,
  nextProps,
  completeProps,
  cancelProps
}) => {
  const {
    isFirstStep,
    isLastStep,
    goBack,
    goNext,
    complete,
    cancel,
    isLoading
  } = props;

  return (
    <>
      {showCancel && (
        <ModalCancelButton
          {...cancelProps}
          onClick={cancel}
          disabled={isLoading}
        >
          {cancelLabel}
        </ModalCancelButton>
      )}
      <div className="flex-1" />
      {!isFirstStep && (
        <ModalCancelButton
          iconLeft={<ChevronLeft className="h-4 w-4" />}
          {...backProps}
          onClick={goBack}
          disabled={isLoading}
        >
          {backLabel}
        </ModalCancelButton>
      )}
      {isLastStep ? (
        <ModalConfirmButton
          {...completeProps}
          onClick={complete}
          isLoading={isLoading}
        >
          {completeLabel}
        </ModalConfirmButton>
      ) : (
        <ModalConfirmButton
          iconRight={<ChevronRight className="h-4 w-4" />}
          {...nextProps}
          onClick={goNext}
          disabled={isLoading}
        >
          {nextLabel}
        </ModalConfirmButton>
      )}
    </>
  );
};

/**
 * WizardModal component for creating multi-step wizards
 */
export const WizardModal: React.FC<WizardModalProps> = (props) => {
  const {
    isOpen,
    onOpenChange,
    steps,
    currentStep: controlledStep,
    onStepChange,
    onComplete,
    onCancel,
    backLabel = "Back",
    nextLabel = "Next",
    completeLabel = "Complete",
    cancelLabel = "Cancel",
    backProps,
    nextProps,
    completeProps,
    cancelProps,
    showCancel = true,
    showProgress = true,
    progressVariant = "steps",
    allowStepClick = false,
    layout = "sidebar",
    decorationIcon: DecorationIcon,
    sidebarFooter,
    bodyHeight,
    asDrawer = false,
    drawerSize = "3xl",
    unmountContent = true,
    contentClassName,
    isLoading: externalLoading = false,
    confirmOnClose = false,
    renderFooter,
    renderProgress,
    title,
    description,
    size,
    ...rest
  } = props;

  const isLoading = useBooleanState(false);
  // A step's validate may hit the network (e.g. it creates the record), so
  // repeated clicks must not run it again while it is in flight.
  const isAdvancingRef = useRef(false);
  const confirmCloseDisclosure = useDisclosure();
  const [internalStep, setInternalStep] = useState(0);

  const isControlled = controlledStep !== undefined;
  const totalSteps = steps.length;

  // Use controlled or internal step, clamped to the current step range. The
  // steps array can shrink (e.g. a conditional step is removed) while the
  // active index is past it; clamping prevents rendering undefined/out-of-range
  // content until navigation corrects the index.
  const currentStep = Math.min(
    controlledStep ?? internalStep,
    Math.max(totalSteps - 1, 0)
  );
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === totalSteps - 1;
  const currentStepData = steps[currentStep];

  // Reset internal step when modal opens
  useEffect(() => {
    if (isOpen && !isControlled) {
      setInternalStep(0);
    }
  }, [isOpen, isControlled]);

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < totalSteps) {
        if (isControlled) {
          onStepChange?.(index);
        } else {
          setInternalStep(index);
          onStepChange?.(index);
        }
      }
    },
    [totalSteps, isControlled, onStepChange]
  );

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  const goNext = useCallback(async () => {
    await runExclusive(isAdvancingRef, isLoading.setState, async () => {
      if (currentStepData?.validate) {
        const isValid = await currentStepData.validate();
        if (!isValid) return;
      }

      if (currentStep < totalSteps - 1) {
        goToStep(currentStep + 1);
      }
    });
  }, [currentStep, totalSteps, currentStepData, goToStep, isLoading]);

  const handleComplete = useCallback(async () => {
    await runExclusive(isAdvancingRef, isLoading.setState, async () => {
      if (currentStepData?.validate) {
        const isValid = await currentStepData.validate();
        if (!isValid) return;
      }

      await onComplete?.();
    });
  }, [currentStepData, onComplete, isLoading]);

  const handleCancel = useCallback(() => {
    if (confirmOnClose) {
      confirmCloseDisclosure.onOpen();
    } else {
      onCancel?.();
      onOpenChange(false);
    }
  }, [confirmOnClose, confirmCloseDisclosure, onCancel, onOpenChange]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleCancel();
        return;
      }
      onOpenChange(true);
    },
    [handleCancel, onOpenChange]
  );

  const loading = isLoading.state || externalLoading;

  const bodyHeightStyle: React.CSSProperties = bodyHeight ?? {
    height: "60vh",
    maxHeight: "600px",
    minHeight: "400px"
  };

  const footerRenderProps: WizardFooterRenderProps = {
    currentStep,
    totalSteps,
    isFirstStep,
    isLastStep,
    canGoBack: !isFirstStep,
    canGoNext: !isLastStep,
    goBack,
    goNext,
    goToStep,
    complete: handleComplete,
    cancel: handleCancel,
    isLoading: loading
  };

  const progressRenderProps: WizardProgressRenderProps = {
    currentStep,
    totalSteps,
    steps,
    goToStep,
    allowStepClick
  };

  // Render progress indicator based on variant
  const renderProgressIndicator = () => {
    if (!showProgress) return null;

    if (renderProgress) {
      return renderProgress(progressRenderProps);
    }

    switch (progressVariant) {
      case "dots":
        return <WizardProgressDots {...progressRenderProps} />;
      case "bar":
        return <WizardProgressBar {...progressRenderProps} />;
      default:
        return <WizardProgressSteps {...progressRenderProps} />;
    }
  };

  const footer = renderFooter ? (
    renderFooter(footerRenderProps)
  ) : (
    <WizardFooter
      props={footerRenderProps}
      backLabel={backLabel}
      nextLabel={nextLabel}
      completeLabel={completeLabel}
      cancelLabel={cancelLabel}
      showCancel={showCancel}
      backProps={backProps}
      nextProps={nextProps}
      completeProps={completeProps}
      cancelProps={cancelProps}
    />
  );

  const content = (
    <div
      className={cn("flex flex-col", contentClassName)}
      style={bodyHeightStyle}
    >
      {/* Fixed header - Progress indicator and step description */}
      <div
        className={cn(
          "shrink-0 border-neutral-200 border-b px-6",
          showProgress &&
            !currentStepData?.description &&
            progressVariant === "steps" &&
            "pb-8"
        )}
      >
        {showProgress && renderProgressIndicator()}
        {currentStepData?.description && (
          <p className="mt-8 mb-4 text-center text-md text-neutral-500">
            {currentStepData.description}
          </p>
        )}
      </div>

      {/* Scrollable content area */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {currentStepData?.content}
      </div>

      {/* Fixed footer - Buttons */}
      {footer && (
        <div className="shrink-0 border-neutral-200 border-t px-6 py-3">
          <div className="flex items-center gap-4">{footer}</div>
        </div>
      )}
    </div>
  );

  // Drawer content without footer (footer is handled by Drawer component)
  const drawerContent = (
    <div className={cn("flex h-full flex-col", contentClassName)}>
      {/* Fixed header - Progress indicator and step description */}
      <div
        className={cn(
          "shrink-0 border-neutral-200 border-b px-6",
          showProgress && progressVariant === "steps" && "pb-8"
        )}
      >
        {showProgress && renderProgressIndicator()}
        {currentStepData?.description && (
          <div className="mt-4 text-center">
            <h3 className="font-inter font-medium text-lg text-neutral-900">
              {currentStepData.label}
            </h3>
            <p className="mt-1 text-neutral-500 text-sm">
              {currentStepData.description}
            </p>
          </div>
        )}
      </div>

      {/* Scrollable content area */}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        {currentStepData?.content}
      </div>
    </div>
  );

  const useSidebar = layout === "sidebar" && !asDrawer;

  // Themis-style two-column layout: descriptive sidebar + step content.
  const sidebarContent = (
    <div className="flex overflow-hidden" style={bodyHeightStyle}>
      <aside className="relative hidden w-64 shrink-0 flex-col overflow-hidden border-neutral-200 border-r bg-neutral-50 px-6 py-4 md:flex">
        {DecorationIcon ? (
          <DecorationIcon
            aria-hidden
            className="pointer-events-none absolute right-[-4rem] bottom-[-1.5rem] z-0 h-40 w-40 text-neutral-200/70"
          />
        ) : null}
        <div className="relative z-10 mb-6">
          {title ? (
            <p className="font-mona font-semibold text-lg text-neutral-900">
              {title}
            </p>
          ) : null}
          {description ? (
            <p className="font-inter mt-1 text-neutral-600 text-[13px] text-sm">
              {description}
            </p>
          ) : null}
        </div>
        <div className="relative z-10 min-h-0 flex-1 overflow-y-auto">
          {showProgress ? (
            <WizardSidebarSteps {...progressRenderProps} />
          ) : null}
        </div>
        {sidebarFooter ? (
          // Pinned to the bottom of the sidebar (flex-1 steps push it down) and
          // kept within the aside's padding so it stays centered — no horizontal
          // bleed.
          <div className="relative z-0 mt-4 shrink-0">{sidebarFooter}</div>
        ) : null}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile fallback: the sidebar is hidden < md, so surface the title and
            a compact stepper here. */}
        <div className="font-mona shrink-0 border-neutral-200 border-b px-6 py-4 md:hidden">
          {title ? (
            <p className="mb-3 font-semibold text-lg text-neutral-900">
              {title}
            </p>
          ) : null}
          {showProgress ? (
            <WizardProgressSteps {...progressRenderProps} />
          ) : null}
        </div>
        <div className="shrink-0 border-neutral-200 px-6 py-4">
          <h3 className="font-mona font-semibold text-[20px] text-neutral-1000 tracking-[-0.01em]">
            {currentStepData?.label}
          </h3>
          {currentStepData?.description ? (
            <p className="font-inter text-[13px] text-neutral-700">
              {currentStepData.description}
            </p>
          ) : null}
        </div>
        <div
          key={currentStepData?.id}
          className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 pb-5"
        >
          {currentStepData?.content}
        </div>
        {footer ? (
          <div className="shrink-0 border-neutral-200 border-t px-6 py-3">
            <div className="flex items-center gap-4">{footer}</div>
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <>
      {asDrawer ? (
        <Drawer
          title={title}
          footer={<div className="flex w-full items-center">{footer}</div>}
          isOpen={isOpen}
          size={drawerSize}
          contentClassName={contentClassName}
          unmountContent={unmountContent}
          onOpenChange={handleOpenChange}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Modal
          title={useSidebar ? undefined : title}
          description={useSidebar ? undefined : description}
          isOpen={isOpen}
          size={size ?? (useSidebar ? "2xl" : undefined)}
          unmountContent={unmountContent}
          onOpenChange={handleOpenChange}
          onClose={() => null}
          // Sidebar layout owns the full height: collapse the modal header band
          // so the sidebar runs edge-to-edge (the close button stays overlaid).
          headerClassName={useSidebar ? "!hidden" : undefined}
          titleClassName={useSidebar ? undefined : "text-center pt-3"}
          innerContentClassName={"p-0"}
          {...rest}
        >
          {useSidebar ? sidebarContent : content}
        </Modal>
      )}
      <ConfirmModal
        onOpenChange={confirmCloseDisclosure.onOpenChange}
        isOpen={confirmCloseDisclosure.isOpen}
        title="Are you sure you want to close?"
        message="Your progress will be lost. Are you sure you want to close?"
        onConfirm={() => {
          onCancel?.();
          onOpenChange(false);
          confirmCloseDisclosure.onClose();
        }}
        confirmProps={{ severity: "critical" }}
        confirmLabel="Close"
      />
    </>
  );
};
